import _ from "lodash";
import { isSubjectToVTA } from "../services/vta.services";

export default abstract class InvoiceThemeService {
    static getDefaultTableColumns() {
        // productReference (optional)
        // productTitle
        // quantity (optional)
        // unitPrice (optional)
        // vatRate (optional)
        // vatAmount (optional)
        // netAmount
        // discount (% or amount)

        // netAmount = quantity * unitPrice
        // grossAmount = netAmount + vatAmount
        // vatAMount = netAmount * vatRate / 100
        return [
            {
                checked: false,
                optional: true,
                text: "Référence",
                column: "Référence",
                value: "reference",
                align: "left",
                width: 15,
                boldText: false,
                type: null,
            },
            {
                checked: true,
                optional: false,
                text: "Produit",
                column: "Produit",
                value: "product",
                align: "left",
                width: 40,
                boldText: false,
                type: null,
            },
            {
                checked: true,
                optional: false,
                text: "Quantité",
                column: "Quantité",
                value: "quantity",
                align: "center",
                width: 10,
                boldText: false,
                type: "number",
            },
            {
                checked: false,
                optional: true,
                text: "Type",
                column: "Type (stock)",
                value: "type",
                align: "center",
                width: 10,
                boldText: false,
                type: null,
            },
            {
                checked: true,
                optional: false,
                text: "Prix unitaire",
                column: "Prix unitaire",
                value: "unitPrice",
                align: "center",
                width: 15,
                boldText: false,
                type: "amount",
            },
            {
                checked: true,
                optional: false,
                text: "Taux TVA",
                column: "Taux TVA",
                value: "vatRate",
                align: "center",
                width: 10,
                boldText: false,
                type: "percent",
            },
            {
                checked: false,
                optional: true,
                text: "TVA",
                column: "TVA",
                value: "vatAmount",
                align: "center",
                width: 5,
                boldText: false,
                type: "amount",
            },
            {
                checked: true,
                optional: false,
                text: "Remise",
                column: "Remise",
                value: "discount",
                align: "center",
                width: 10,
                boldText: false,
                type: "percent",
            },
            {
                checked: true,
                optional: true,
                text: "Montant HT",
                column: "Montant HT",
                value: "netAmount",
                align: "right",
                width: 15,
                boldText: true,
                type: "amount",
            },
            {
                checked: false,
                optional: true,
                text: "Montant TTC",
                column: "Montant TTC",
                value: "grossAmount",
                align: "right",
                width: 15,
                boldText: true,
                type: "amount",
            },
        ];
    }

    static selectDefaultTableColumns(selections: string[] | null = null) {
        if (!selections) {
            return this.getDefaultTableColumns();
        }

        return this.getDefaultTableColumns().filter((column) => selections.includes(column.value));
    }

    static defaultOrderedColumnsValues() {
        return [
            "reference",
            "product",
            "quantity",
            "unitPrice",
            "vatRate",
            "vatAmount",
            "discount",
            "netAmount",
            "grossAmount",
        ];
    }

    static getDefaultVATTableColumns() {
        return [
            {
                checked: false,
                optional: true,
                text: "Net",
                column: "Net",
                value: "netAmount",
                align: "left",
                width: 33,
                boldText: false,
                type: "amount",
            },
            {
                checked: true,
                optional: false,
                text: "Taux TVA",
                column: "Taux TVA",
                value: "vatRate",
                align: "left",
                width: 33,
                boldText: false,
                type: "percent",
            },
            {
                checked: true,
                optional: false,
                text: "TVA",
                column: "TVA",
                value: "vatAmount",
                align: "left",
                width: 33,
                boldText: false,
                type: "amount",
            },
        ];
    }

    static getAllVATTableColumns() {
        return [
            {
                checked: true,
                optional: true,
                text: "Net",
                column: "Net",
                value: "netAmount",
                align: "left",
                width: 33,
                type: "amount",
            },
            {
                checked: true,
                optional: false,
                text: "Taux TVA",
                column: "Taux TVA",
                value: "vatRate",
                align: "left",
                width: 33,
                type: "percent",
            },
            {
                checked: true,
                optional: false,
                text: "TVA",
                column: "TVA",
                value: "vatAmount",
                align: "left",
                width: 33,
                type: "amount",
            },
        ];
    }

    static getDefaultInvoiceThemeSettings() {
        return {
            version: 3, // V3 of settings
            useAdvancedStyling: false,
            general: {
                measurementUnit: "mm",
                pageFormat: "A4",
                pageOrientation: "portrait",
            },
            page: {
                repeatOnFollowingPages: {
                    header: true,
                    footer: true,
                    emitter: false,
                    receiver: false,
                    titleBox: false, // titleBox
                    invoiceNumberBox: true,
                },

                horizontalMargin: 1,
                verticalMargin: 1,
                fontFamily: "Par défaut",
                fontSize: 11, // unit is pt,
                showPageHeader: true,
                useBackgroundImage: false,
                selectedDefaultBackgroundKey: null, // When a default background is selected, this is set to the name/id of the default background
                backgroundSize: "cover", // cover, contain, auto
                backgroundRepeat: "repeat", // repeat, no-repeat, repeat-x, repeat-y
                backgroundPosition: "top", // top, center, bottom    ; We only consider these values usage simplicity  // TODO check if it is enough
                backgroundOpacity: 100, // Opacity in percentage
            },
            headerContentBox: {
                showHeaderContent: true, // When applicable
                style: {
                    // Advanced styling options
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#ffffff00",
                    borderRadius: 0,

                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
                iconBox: {
                    showIcon: true,
                    logoSizeMultiplier: 1,
                    iconPosition: "left", // "left" | "center" | "right"
                    // TODO proposition: allow to create a text logo (using font-family and font-size)
                    style: {
                        // fontFamily:null,
                        // fontWeight:null,
                        // fontSize:null,
                        // color:null,
                        // border:false,
                        // backgroundColor:"#ffffff00",
                        // borderColor:null,
                        // textPosition: "left",

                        // Advanced styling options
                        color: null,
                        border: false,
                        backgroundColor: "#ffffff00",
                        borderColor: "#ffffff00",
                        borderRadius: 0,

                        hidden: false,
                        boxHeight: null,
                        boxWidth: null,
                        leftMargin: null,
                        rightMargin: null,
                        topMargin: null,
                        bottomMargin: null,
                        padding: null,
                    },
                },
                invoiceTypeBox: {
                    showInvoiceType: true,
                    useInvoiceNumber: false,
                    style: {
                        fontFamily: null,
                        fontWeight: null,
                        fontSize: null,
                        color: null,
                        border: false,
                        backgroundColor: "#ffffff00",
                        borderColor: "#ffffff00",
                        textPosition: "right",
                        borderRadius: 0,

                        // Advanced styling options
                        hidden: false,
                        boxHeight: null,
                        boxWidth: null,
                        leftMargin: null,
                        rightMargin: null,
                        topMargin: null,
                        bottomMargin: null,
                        padding: null,
                    },
                },
            },
            emitter: {
                name: true,
                address: true,
                siret: true,
                tva: true,
                logo: false,
                legalForm: null,
                email: false,
                phone: false,
                style: {
                    fontFamily: null,
                    fontWeight: null,
                    fontSize: null,
                    color: "#000000FF", // Text color
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#000000",
                    borderRadius: 0,
                    textPosition: "left",

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            receiver: {
                name: true,
                address: true,
                siret: true,
                tva: true,
                logo: null,
                email: null,
                phone: false,
                style: {
                    fontFamily: null,
                    fontWeight: null,
                    fontSize: null,
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#000000",
                    borderRadius: 0,
                    textPosition: "right",

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            titleBox: {
                showTitle: true,
                showDescription: true,
                style: {
                    fontFamily: null,
                    fontWeight: null,
                    fontSize: null,
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#ffffff00",
                    borderRadius: 0,
                    textPosition: "left",

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            mainTableBox: {
                // tableRowsStyle: "default",  // "default", "simple", "alternating"
                // secondaryRowsBackgroundColor: "",
                automaticColors: true,

                header: {
                    color: "",
                    fontFamily: "",
                    // fontColor:"",
                    fontWeight: "",
                    borderColor: "",
                    borderBottom: null,
                    backgroundColor: "#355269FF",
                },

                rows: {
                    border: true,
                    color: "",
                    fontWeight: "",
                    borderBottom: null,
                    borderColor: "#efefef",
                    backgroundColor: "#ffffff",
                    secondaryBackgroundColor: "#FFFFFFFF",
                    mode: "default", // "default", "simple", "alternating"
                    showDiscountedAmount: true,
                    crossNetAmount: true, // When showDiscountedAmount amount is set
                    showPercentDiscountAmount: true,
                    hideProductionDescription: false,
                },

                style: {
                    // fontFamily:null,
                    // fontWeight:null,
                    // fontSize:null,
                    // color:null,
                    // border:false,
                    // backgroundColor:"#ffffff00",
                    // borderColor:null,
                    borderRadius: 0,

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            depositsBox: {
                showDeposits: true, // When applicable
                style: {
                    fontFamily: null,
                    fontWeight: null,
                    fontSize: null,
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#ffffff00",
                    borderRadius: 0,
                    textPosition: "left",

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            vatTableBox: {
                showVATSummary: false,
                automaticColors: true,
                // secondaryRowsBackgroundColor: "",
                // backgroundColor: "#ffffff00",

                // headerBackgroundColor:"#355269",

                header: {
                    color: "",
                    fontFamily: "",
                    // fontColor:"",
                    fontWeight: "",
                    borderColor: "",
                    borderBottom: null,
                    backgroundColor: "#355269",
                },

                rows: {
                    border: true,
                    color: "",
                    fontWeight: "",
                    borderBottom: null,
                    borderColor: "#efefef",
                    backgroundColor: "#ffffff",
                    secondaryBackgroundColor: "#FFFFFFFF",
                    mode: "default", // "default", "simple", "alternating"
                },

                style: {
                    // fontFamily:null,
                    // fontWeight:null,
                    // fontSize:null,
                    // color:null,
                    // border:false,
                    // backgroundColor:"#ffffff00",
                    // borderColor:null,

                    // Advanced styling options
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#ffffff00",
                    borderRadius: 0,

                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            totalsBox: {
                showTotals: true,
                showDiscount: true, // If applicable
                style: {
                    // fontFamily:null,
                    // fontWeight:null,
                    // fontSize:null,
                    // color:null,
                    // border:false,
                    // backgroundColor:"#ffffff00",
                    // borderColor:null,

                    // Advanced styling options
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#ffffff00",
                    borderRadius: 0,

                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            paymentDetailsBox: {
                showPaymentDetails: true, // When applicable
                style: {
                    fontFamily: null,
                    fontWeight: null,
                    fontSize: null,
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#000000FF",
                    borderRadius: 0,
                    textPosition: "left",

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: 5,
                    bottomMargin: null,
                    padding: null,
                },
            },
            paginationBox: {
                alwaysShow: false, // Always visible even when there is a single page
                position: "right", // left, center, right
                style: {
                    // fontFamily:null,
                    // fontWeight:null,
                    // fontSize:null,
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#000000FF",
                    borderRadius: 0,

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: 2,
                },
            },
            footerContentBox: {
                showFooterContent: true, // When applicable
                style: {
                    fontFamily: null,
                    fontWeight: null,
                    fontSize: null,
                    color: null,
                    border: false,
                    backgroundColor: "#ffffff00",
                    borderColor: "#000000FF",
                    borderRadius: 0,
                    textPosition: "center",

                    // Advanced styling options
                    hidden: false,
                    boxHeight: null,
                    boxWidth: null,
                    leftMargin: null,
                    rightMargin: null,
                    topMargin: null,
                    bottomMargin: null,
                    padding: null,
                },
            },
            style: {
                fontFamily: null,
                fontWeight: null,
                fontSize: null,
                color: null,
                border: true,
                backgroundColor: "#efefef",
                borderColor: null,
                logoHeader: true,
                displayHeaderDivider: true,
                displayFooterDivider: null,
                // logoSizeMultiplier: 1,  // TODO remove after replacement
            },
            invoice: {
                name: null,
                number: null,
                description: null,
                netAmounts: null,
                taxAmounts: null,
                totalAmount: null,
                date: null,
                dueDate: null,
                paymentConditions: null,
                paymentMethod: null,
                miscellaneous: null,
            },
            position: {
                emitter: {
                    sameLevelAsReceiver: null,
                },
                receiver: {
                    name: null,
                    address: null,
                    siret: null,
                    tva: null,
                    logo: null,
                    email: null,
                },
                invoice: {
                    name: null,
                    number: null,
                    description: null,
                    netAmounts: null,
                    taxAmounts: null,
                    totalAmount: null,
                    date: null,
                    dueDate: null,
                    paymentConditions: null,
                    paymentMethod: null,
                    miscellaneous: null,
                },
            },
            tables: {
                header: {
                    fontFamily: "",
                    fontColor: "",
                    fontWeight: "",
                    borderColor: "",
                    borderBottom: null,
                    backgroundColor: "#355269",
                    columns: JSON.parse(JSON.stringify(this.getDefaultTableColumns())),
                },
                rows: {
                    border: true,
                    fontWeight: "",
                    borderBottom: null,
                    borderColor: "#efefef",
                    backgroundColor: "#ffffff",
                },
                footer: {
                    fontFamily: "",
                    fontWeight: "",
                    borderColor: "",
                    totalColorSeparator: "#f9b128",
                    borderTop: null,
                    backgroundColor: "#efefef",
                },
            },
            summary: {
                showDiscount: false, // TODO remove after replacement
                showVATSummary: false, // TODO remove after replacement
                vatTable: {
                    columns: JSON.parse(JSON.stringify(this.getDefaultVATTableColumns())),
                },
                discountType: "percent", //"percent" | "amount"
            },
            appendix: {
                includeAppendix: false,
            },
        };
    }

    static bobbeeDefaultTheme() {
        return InvoiceThemeService.mergeWithDefaultSettings({
            useAdvancedStyling: true,
            page: {
                horizontalMargin: 10,
                verticalMargin: 10,
                fontSize: 11,
                useBackgroundImage: false,
            },
            headerContentBox: {
                iconBox: {
                    logoSizeMultiplier: 0.5,
                },

                invoiceTypeBox: {
                    useInvoiceNumber: false,
                    style: {
                        // backgroundColor:"#355269ff",
                        // borderColor: "#355269",
                        // boxHeight: 17,
                        // padding: 2,
                        // color: "#ffffff",
                    },
                },
            },
            style: {
                displayHeaderDivider: false,
                displayFooterDivider: false,
            },
        });
    }

    static v2DefaultTheme() {
        return InvoiceThemeService.mergeWithDefaultSettings({
            name: "Par défaut",
            useAdvancedStyling: true,
            page: {
                horizontalMargin: 10,
                verticalMargin: 10,
                fontSize: 10,
                useBackgroundImage: false,
                fontFamily: "Tahoma",
            },
            headerContentBox: {
                iconBox: {
                    logoSizeMultiplier: 0.5,
                },

                invoiceTypeBox: {
                    useInvoiceNumber: false,
                    style: {
                        // backgroundColor:"#355269ff",
                        // borderColor: "#355269",
                        // boxHeight: 17,
                        // padding: 2,
                        // color: "#ffffff",
                    },
                },
            },
            titleBox: {
                style: {
                    bottomMargin: "3",
                },
            },

            // mainTableBox: {
            //     header:{
            //         backgroundColor:"#355269FF"
            //     },

            //     rows:{
            //         border: false,
            //         backgroundColor:"#EFEFEFFF",
            //         secondaryBackgroundColor: "#FFFFFFFF",
            //         mode: "alternating",  // "default", "simple", "alternating"
            //     },
            // },
            tables: {
                header: {
                    columns: [
                        {
                            checked: true,
                            optional: false,
                            text: "Produit",
                            column: "Produit",
                            value: "product",
                            align: "left",
                            width: 45,
                            type: null,
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "Qté",
                            column: "Quantité",
                            value: "quantity",
                            align: "center",
                            width: 10,
                            type: "number",
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "Prix unitaire",
                            column: "Prix unitaire",
                            value: "unitPrice",
                            align: "center",
                            width: 15,
                            type: "amount",
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "TVA",
                            column: "Taux TVA",
                            value: "vatRate",
                            align: "center",
                            width: 10,
                            type: "percent",
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "Remise",
                            column: "Remise",
                            value: "discount",
                            align: "center",
                            width: 15,
                            type: "percent",
                        },
                        {
                            checked: true,
                            optional: true,
                            text: "Montant HT",
                            column: "Montant HT",
                            value: "netAmount",
                            align: "right",
                            width: 15,
                            type: "amount",
                        },
                        {
                            checked: false,
                            optional: true,
                            text: "TVA",
                            column: "TVA",
                            value: "vatAmount",
                            align: "center",
                            width: 5,
                            type: "amount",
                        },
                        {
                            checked: false,
                            optional: true,
                            text: "Montant TTC",
                            column: "Montant TTC",
                            value: "grossAmount",
                            align: "right",
                            width: 20,
                            type: "amount",
                        },
                        {
                            checked: false,
                            optional: true,
                            text: "Référence",
                            column: "Référence",
                            value: "reference",
                            align: "left",
                            width: 20,
                            type: null,
                        },
                    ],
                },
                rows: {
                    border: true,
                    // borderColor: "#AAAAAAFF",
                },
            },
            style: {
                displayHeaderDivider: true,
                displayFooterDivider: false,
            },

            // tables: {
            //     header:{
            //         backgroundColor:"#355269",
            //     },
            //     rows:{
            //         border: false,
            //         backgroundColor:"#EFEFEFFF",
            //     },
            // }
        });
    }

    static originalTheme() {
        // V1Theme
        return InvoiceThemeService.mergeWithDefaultSettings({
            name: "D'origine",
            useAdvancedStyling: true,
            page: {
                horizontalMargin: 15,
                verticalMargin: 10,
                fontSize: 10,
                useBackgroundImage: false,
                fontFamily: "Tahoma",
            },
            headerContentBox: {
                iconBox: {
                    logoSizeMultiplier: 0.5,
                },

                invoiceTypeBox: {
                    useInvoiceNumber: false,
                    style: {},
                },
            },
            titleBox: {
                style: {
                    bottomMargin: "3",
                },
            },
            tables: {
                header: {
                    columns: [
                        {
                            checked: true,
                            optional: false,
                            text: "Produit",
                            column: "Produit",
                            value: "product",
                            align: "left",
                            width: 45,
                            type: null,
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "Qté",
                            column: "Quantité",
                            value: "quantity",
                            align: "center",
                            width: 10,
                            type: null,
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "Prix unitaire",
                            column: "Prix unitaire",
                            value: "unitPrice",
                            align: "center",
                            width: 15,
                            type: "amount",
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "TVA",
                            column: "Taux TVA",
                            value: "vatRate",
                            align: "center",
                            width: 10,
                            type: "percent",
                        },
                        {
                            checked: true,
                            optional: false,
                            text: "Remise",
                            column: "Remise",
                            value: "discount",
                            align: "center",
                            width: 15,
                            type: "percent",
                        },
                        {
                            checked: true,
                            optional: true,
                            text: "Montant HT",
                            column: "Montant HT",
                            value: "netAmount",
                            align: "right",
                            width: 15,
                            type: "amount",
                        },
                        {
                            checked: false,
                            optional: true,
                            text: "TVA",
                            column: "TVA",
                            value: "vatAmount",
                            align: "center",
                            width: 5,
                            type: "amount",
                        },
                        {
                            checked: false,
                            optional: true,
                            text: "Montant TTC",
                            column: "Montant TTC",
                            value: "grossAmount",
                            align: "right",
                            width: 20,
                            type: "amount",
                        },
                        {
                            checked: false,
                            optional: true,
                            text: "Référence",
                            column: "Référence",
                            value: "reference",
                            align: "left",
                            width: 20,
                            type: null,
                        },
                    ],
                },
                rows: {
                    border: true,
                    borderColor: "#AAAAAAFF",
                },
            },
            style: {
                displayHeaderDivider: true,
                displayFooterDivider: false,
            },
        });
    }

    static v1Theme() {
        return InvoiceThemeService.originalTheme();
    }

    /**
     * Custom merger tailored for invoice theme settings.
     * It handles the lists specifically: lists are composed of objects with a unique key 'value'.
     * Objects are merged with respect to this value. If an object is in default and not in custom,
     * it is added to custom. If an object is in custom but not in default, it is removed from custom.
     * Default acts like a model.
     * Objects are not modified.
     * @param defaultObj
     * @param customObj
     * @returns merged Object
     */
    static customMerge(defaultObj, customObj) {
        return _.mergeWith({}, defaultObj, customObj, (objValue, srcValue, key) => {
            // Check if the values are arrays
            // if (_.isArray(objValue) && _.isArray(srcValue)) {
            //     // Create a map of the personalized array by value for quick lookups
            //     const srcValueMap = _.keyBy(srcValue, 'value');

            //     // Merge arrays while preserving order and relative position
            //     const mergedArray = objValue.map(defaultItem => {
            //         const correspondingSrcItem = srcValueMap[defaultItem.value];
            //         // If the item exists in personalized settings, merge it; otherwise, use the default item
            //         return correspondingSrcItem ? _.merge({}, defaultItem, correspondingSrcItem) : defaultItem;
            //     });

            //     // Add any new items from the personalized array that aren't in the default array
            //     // const newItems = srcValue.filter(srcItem => !_.find(objValue, { value: srcItem.value }));
            //     // console.log("newItems", newItems)
            //     // Find the correct position for new items (in custom) based on the default array's structure
            //     //   newItems.forEach(newItem => {
            //     //     const insertIndex = objValue.findIndex(item => item.value === newItem.value);
            //     //     mergedArray.splice(insertIndex, 0, newItem);
            //     //   });

            //     return mergedArray;
            // }

            if (_.isArray(objValue) && _.isArray(srcValue)) {
                const defaultValueMap = _.keyBy(objValue, "value");

                const mergedArray = srcValue.map((srcItem) => {
                    const correspondingDefaultItem = defaultValueMap?.[srcItem.value]; //_.find(objValue, { value: srcItem.value });
                    return correspondingDefaultItem
                        ? _.merge({}, correspondingDefaultItem, srcItem)
                        : null;
                });

                return mergedArray.filter((x) => x);
            }
        });
    }

    /**
     * Merge two settings objects. Personnal settings take precedence.
     * Keys in default settings which are not in personal setting are added to personal settings.
     * The objects are not modified, a new object is returned.
     * @param personalSettings
     * @param defaultSettings
     * @returns Object
     */
    static mergeSettings(personalSettings, defaultSettings) {
        const destination = JSON.parse(JSON.stringify(defaultSettings));
        return InvoiceThemeService.customMerge(destination, personalSettings); //destination
        // _.merge(destination, personalSettings)
        // for (const key in newSettings) {
        //     if (newSettings.hasOwnProperty(key)) {
        //         if (typeof newSettings[key] === 'object' && !Array.isArray(newSettings[key])) {
        //             if (settings[key] === undefined) {
        //                 settings[key] = {}
        //             }
        //             this.mergeSettings(settings[key], newSettings[key])
        //         } else if(Array.isArray(newSettings[key])) {
        //             for (let i = 0; i < newSettings[key].length; i++) {
        //                 if (settings[key] === undefined) {
        //                     settings[key] = []
        //                 }
        //                 if (newSettings[key][i] !== undefined) {
        //                     settings[key][i] = newSettings[key][i]
        //                 }
        //             }
        //         } else {
        //             settings[key] = newSettings[key]
        //         }
        //     }
        // }
    }

    /**
     * Merge personal settings with default settings.
     * Keys in default settings which are not in personal setting are added to personal settings.
     * The object are not modified, a new object is returned.
     * @param personalSettings
     * @returns Object
     */
    static mergeWithDefaultSettings(personalSettings) {
        return this.mergeSettings(personalSettings, this.getDefaultInvoiceThemeSettings());
    }

    static mergeWithV2ThemeSettings(personalSettings) {
        return this.mergeSettings(personalSettings, this.v2DefaultTheme());
    }

    /**
     * Deep difference between obj1 and obj2. This is a helper function use objDiff instead
     * diff is modified in place
     * @param obj1
     * @param obj2
     * @param diff
     */
    private static objDiffHelper(obj1, obj2, diff) {
        if (!obj1) {
            return _.assign(diff, obj2);
        }
        for (const key in obj1) {
            if (
                obj1?.[key] !== undefined &&
                typeof obj1[key] !== "object" &&
                obj1?.[key] !== obj2?.[key]
            ) {
                diff[key] = obj1[key];
            }
        }
    }

    /**
     * Deep difference between obj1 and obj2.
     * diff is modified in place
     * @param obj1
     * @param obj2
     * @param diff
     */
    static objDiff(obj1, obj2, diff) {
        if (!_.isEqual(obj1, obj2)) {
            if (Array.isArray(obj1) && Array.isArray(obj2)) {
                let i = 0;
                for (const el of obj1) {
                    diff.push({});
                    InvoiceThemeService.objDiff(el, obj2?.[i], diff[i]);
                    i++;
                }
            } else if (Array.isArray(obj1) && !Array.isArray(obj2)) {
                diff = [...obj1];
            } else if (!Array.isArray(obj1) && Array.isArray(obj2)) {
                _.assign(diff, obj1);
            } else if (typeof obj1 === "object") {
                InvoiceThemeService.objDiffHelper(obj1, obj2, diff);
                for (const key in obj1) {
                    if (typeof obj1[key] === "object") {
                        diff[key] = Array.isArray(obj1[key]) ? [] : {};
                        const res = this.objDiff(obj1[key], obj2?.[key], diff[key]);
                    }
                }
            }
        }
    }

    /**
     * Format color to hex + opacity (ex: #ffffff80)
     * @param color
     */
    static formatColorToHexa(color: string | null | undefined) {
        let value = color;
        if (value) {
            value = value.trim();
            if (!value.startsWith("#")) {
                value = "#" + value;
            }
            if (value.length <= 7) {
                // #000000
                value += "ff"; // #000000FF
            }
        }
        return value;
    }

    static defaultLegalForm(company) {
        let legalForm = "";
        if (company) {
            legalForm = company.name;
            if (company.shareCapital) {
                legalForm += " au capital social de " + company.shareCapital + " €";
            }
            let address = "";
            if (company.address) {
                address = company.address;
            }
            if (company.postalCode) {
                address += " " + company.postalCode;
            }
            if (company.city) {
                address += " " + company.city;
            }
            if (address) {
                legalForm += " - " + address;
            }
            if (company.registrationTown) {
                legalForm += " - RCS " + company.registrationTown;
                legalForm += " " + company.siren;
            }
            if (company.activities && company.activities.length) {
                legalForm += " - APE " + company.activities[0].name;
            }
            if (company.vatNumber) {
                legalForm += " - TVA Intracommunautaire " + company.vatNumber;
            }
        }
        return legalForm;
    }

    /**
     * Invalid columns for invoice type. They will not be displayed for this invoice type.
     * @param invoiceType "invoice" | "deposit" | "deliveryNote" | null | undefined
     * @returns columns list
     */
    static invalidColumsForInvoice({
        invoiceType = "",
        invoiceData = null,
        invoiceCompany = null,
    }) {
        let baseColumns = ["type"];
        if (!this.showVatInfo({ invoiceData, invoiceCompany })) {
            baseColumns.push("vatAmount", "vatRate");
        }
        if (!this.showDiscountInfo({ invoiceData })) {
            baseColumns.push("discount", "discountPercentage");
        }
        if (invoiceType === "deposit") {
            baseColumns.push(
                "quantity",
                "discount",
                "discountPercentage",
                "vatAmount",
                "vatRate",
                "unitPrice",
                "reference"
            );
        }
        return Array.from(new Set(baseColumns));
    }

    static showVatInfo({ invoiceData = null, invoiceCompany = null }) {
        return (
            isSubjectToVTA(invoiceCompany?.vatSystem) &&
            (invoiceData?.lines ?? []).reduce((total, line) => total + line.vatAmount, 0)
        );
    }

    static showDiscountInfo({ invoiceData }) {
        return (
            invoiceData?.lines?.reduce((total, line) => total + (line.discount ?? 0), 0) ||
            invoiceData?.lines?.reduce(
                (total, line) => total + (line.discountPercentage ?? 0),
                0
            ) ||
            invoiceData?.discount ||
            invoiceData?.discountPercentage
        );
    }

    static getDocumentTypeFriendlyName(invoiceData) {
        if (invoiceData.type === "INVOICE") {
            return "Facture";
        } else if (invoiceData.type === "DEPOSIT" || invoiceData.depositOf) {
            return "Acompte";
        } else if (invoiceData.type === "BILL_DELIVERY") {
            return "Bordereau de livraison";
        } else if (invoiceData.type === "CREDIT_NOTE") {
            return "Avoir";
        } else if (invoiceData.type === "QUOTATION") {
            return "Devis";
        } else if (invoiceData.type === "INVENTORY") {
            return "Inventaire des stocks";
        } else {
            return invoiceData.type;
        }
    }

    static getDefaultBackgroundsUris() {
        return [
            {
                name: "Par défaut 1",
                fileKey: "invoice-assets/background_1.png",
                src: "",
            },
            {
                name: "Par défaut 2",
                fileKey: "invoice-assets/background_2.png",
                src: "",
            },
            {
                name: "Par défaut 3",
                fileKey: "invoice-assets/background_3.png",
                src: "",
            },
            {
                name: "Par défaut 4",
                fileKey: "invoice-assets/background_4.png",
                src: "",
            },
            {
                name: "Par défaut 5",
                fileKey: "invoice-assets/background_5.png",
                src: "",
            },
        ];
    }

    static getSelectedBackgroundKey(identifier) {
        return this.getDefaultBackgroundsUris().find((background) => background.name === identifier)
            ?.fileKey;
    }

    static defaultRenderingInfo() {
        return {
            document: {},
            page: {},
            header: { id: "#header" }, // #header
            footer: { id: "#footer" }, // #footer
            emitterReceiver: { id: "#emitter-receiver" }, // #emitter-receiver
            invoiceInfo: { id: "#invoiceInfo" }, // #invoiceInfo
            extraPageTitle: { id: "#extraPageTitle" }, // #extraPageTitle
            mainTable: {
                id: "#mainTableBlock",
                thead: {},
                tbody: {
                    rows: [],
                },
                tfoot: {},
            }, // #mainTableBlock     < table.mainTable
            deposits: { id: "#deposits" }, // #deposits
            vatTable: { id: "#vatTable" }, // #vatTable
            totals: { id: "#totals" }, // #totals
            vatAndTotals: { id: "#vatAndTotals" }, // #vatAndTotals
            paymentDetails: { id: "#paymentDetails" }, // #paymentDetails
            pagination: { id: "#pagination" }, // #pagination
        };
    }

    abstract setObjInfoFromEl(obj, el): any;

    abstract extractPageSizeInfoFromDom(invoicePageRef, renderingInfo): any;
}
